import React, { useState, useEffect, useRef, useCallback } from "react";
import GlobalStyles from "../GlobalStyles";
const PUBLIC_BASE_PATH = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
const API_URL = (
  process.env.REACT_APP_API_URL || `${PUBLIC_BASE_PATH}/backend`
).replace(/\/$/, "");

const Scenario2 = ({
  lph,
  maxMonthlyWaterLiters,
  pipeSizes,
  pipeClasses,
  selectedClass,
  classesByMaterial,
  allPipeSizes,
  onDataChange,
  onCalculationResults,
}) => {

  const [suctionPipeSize, setSuctionPipeSize] = useState("");
  const [suctionHead, setSuctionHead] = useState(5);
  const [deliveryHead, setDeliveryHead] = useState(0);
  const [elevationDiff, setElevationDiff] = useState(0);
  const [suctionPipeLength, setSuctionPipeLength] = useState(50);
  const [suctionPipeClass, setSuctionPipeClass] = useState("");
  const [selectedPump, setSelectedPump] = useState("surface");

  const [deliveryPipeSize, setDeliveryPipeSize] = useState("");
  const [deliveryPipeLength, setDeliveryPipeLength] = useState(50);
  const [deliveryPipeClass, setDeliveryPipeClass] = useState("");

  const [fieldPipeSize, setFieldPipeSize] = useState("");
  const [fieldPipeLength, setFieldPipeLength] = useState(100);
  const [fieldPipeClass, setFieldPipeClass] = useState("");

  const dataChangeCallbackEnabled = useRef(true);
  const [calculationResults, setCalculationResults] = useState(null);

  const [localLph, setLocalLph] = useState(lph);

  const [lphInputValue, setLphInputValue] = useState(lph.toString());

  const [numberOfDays, setNumberOfDays] = useState(30);
  const [numberOfHours, setNumberOfHours] = useState(6);
  const [daysInputValue, setDaysInputValue] = useState("30");
  const [hoursInputValue, setHoursInputValue] = useState("6");
  const [maxWaterInputValue, setMaxWaterInputValue] = useState(
    maxMonthlyWaterLiters.toString()
  );

  const [pumpEfficiency, setPumpEfficiency] = useState(60);
  const [motorEfficiency, setMotorEfficiency] = useState(80);
  const [solarEfficiency, setSolarEfficiency] = useState(70);
  const [pumpEfficiencyInput, setPumpEfficiencyInput] = useState("60");
  const [motorEfficiencyInput, setMotorEfficiencyInput] = useState("80");
  const [solarEfficiencyInput, setSolarEfficiencyInput] = useState("70");

  const [suctionPipeDiameter, setSuctionPipeDiameter] = useState(0);
  const [deliveryPipeDiameter, setDeliveryPipeDiameter] = useState(0);
  const [fieldPipeDiameter, setFieldPipeDiameter] = useState(0);

  const [suctionPipeSpecs, setSuctionPipeSpecs] = useState(null);
  const [deliveryPipeSpecs, setDeliveryPipeSpecs] = useState(null);
  const [fieldPipeSpecs, setFieldPipeSpecs] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suctionClassError, setSuctionClassError] = useState(false);
  const [deliveryClassError, setDeliveryClassError] = useState(false);
  const [fieldClassError, setFieldClassError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [suctionErrorMessage, setSuctionErrorMessage] = useState("");
  const [deliveryErrorMessage, setDeliveryErrorMessage] = useState("");
  const [fieldErrorMessage, setFieldErrorMessage] = useState("");

  const [suctionFilteredSizes, setSuctionFilteredSizes] = useState([]);
  const [deliveryFilteredSizes, setDeliveryFilteredSizes] = useState([]);
  const [fieldFilteredSizes, setFieldFilteredSizes] = useState([]);

  const [localSelectedClass, setLocalSelectedClass] = useState(
    selectedClass || ""
  );
  const [isLocalClassChange, setIsLocalClassChange] = useState(false);

  const extractThicknessFromSpecs = (pipeSpecs, classType) => {
    if (!pipeSpecs) return null;

    if (pipeSpecs.value !== undefined) {
      return parseFloat(pipeSpecs.value);
    }

    if (pipeSpecs.dimensions && pipeSpecs.dimensions.length > 0) {
      const thicknessValue = pipeSpecs.dimensions[0][classType];
      if (
        thicknessValue &&
        thicknessValue !== "Unavailable Pipe Dia"
      ) {
        return parseFloat(thicknessValue);
      }
    }

    if (pipeSpecs.classes && pipeSpecs.classes[classType]) {
      return parseFloat(pipeSpecs.classes[classType]);
    }

    return null;
  };

  const getAvailableSizesForClass = useCallback(
    async (classType) => {
      if (!classType) return allPipeSizes;

      try {
        const response = await fetch(
          `${API_URL}/api/v1/pipe/available-sizes/${classType}`
        );

        if (response.ok) {
          const data = await response.json();
          return data.available_sizes || [];
        }
        return allPipeSizes;
      } catch (error) {
        console.error(`Error fetching sizes for class ${classType}:`, error);
        return allPipeSizes;
      }
    },
    [allPipeSizes]
  );

  useEffect(() => {
    const updateSuctionSizes = async () => {
      if (suctionPipeClass) {
        const sizes = await getAvailableSizesForClass(suctionPipeClass);
        setSuctionFilteredSizes(sizes);

        if (suctionPipeSize) {
          const isAvailable = sizes.some(size => {

            const sizeValue = typeof size === 'object' ? size.inch : size;
            return sizeValue === suctionPipeSize;
          });
          if (!isAvailable) {
            setSuctionPipeSize("");
          }
        }
      } else {
        setSuctionFilteredSizes([]);
      }
    };

    updateSuctionSizes();
  }, [
    suctionPipeClass,
    allPipeSizes,
    suctionPipeSize,
    getAvailableSizesForClass,
  ]);

  useEffect(() => {
    const updateDeliverySizes = async () => {
      if (deliveryPipeClass) {
        const sizes = await getAvailableSizesForClass(deliveryPipeClass);
        setDeliveryFilteredSizes(sizes);

        if (deliveryPipeSize) {
          const isAvailable = sizes.some(size => {

            const sizeValue = typeof size === 'object' ? size.inch : size;
            return sizeValue === deliveryPipeSize;
          });
          if (!isAvailable) {
            setDeliveryPipeSize("");
          }
        }
      } else {
        setDeliveryFilteredSizes([]);
      }
    };

    updateDeliverySizes();
  }, [
    deliveryPipeClass,
    allPipeSizes,
    deliveryPipeSize,
    getAvailableSizesForClass,
  ]);

  useEffect(() => {
    const updateFieldSizes = async () => {
      if (fieldPipeClass) {
        const sizes = await getAvailableSizesForClass(fieldPipeClass);
        setFieldFilteredSizes(sizes);

        if (fieldPipeSize) {
          const isAvailable = sizes.some(size => {

            const sizeValue = typeof size === 'object' ? size.inch : size;
            return sizeValue === fieldPipeSize;
          });
          if (!isAvailable) {
            setFieldPipeSize("");
          }
        }
      } else {
        setFieldFilteredSizes([]);
      }
    };

    updateFieldSizes();
  }, [fieldPipeClass, allPipeSizes, fieldPipeSize, getAvailableSizesForClass]);

  useEffect(() => {
    setSuctionFilteredSizes([]);
    setDeliveryFilteredSizes([]);
    setFieldFilteredSizes([]);
  }, [allPipeSizes]);

  useEffect(() => {
    if (!isLocalClassChange) {
      setLocalSelectedClass(selectedClass || "");
    }
    setIsLocalClassChange(false);
  }, [selectedClass, isLocalClassChange]);

  useEffect(() => {
    setLocalLph(lph);
    setLphInputValue(lph.toString());
  }, [lph]);

  useEffect(() => {
    setMaxWaterInputValue(maxMonthlyWaterLiters.toString());
  }, [maxMonthlyWaterLiters]);

  useEffect(() => {
    if (numberOfDays > 0 && numberOfHours > 0) {
      const maxWater = parseFloat(maxWaterInputValue) || maxMonthlyWaterLiters;
      const calculatedLph = maxWater / numberOfDays / numberOfHours;

      const formattedLph = parseFloat(calculatedLph.toFixed(2));

      setLocalLph(formattedLph);
      setLphInputValue(formattedLph.toFixed(2));
    }
  }, [numberOfDays, numberOfHours, maxWaterInputValue, maxMonthlyWaterLiters]);

  useEffect(() => {
    if (suctionPipeSize && suctionPipeClass) {
      fetchPipeDimensions(suctionPipeSize, suctionPipeClass, "suction");
    }
  }, [suctionPipeSize, suctionPipeClass]);

  useEffect(() => {
    if (deliveryPipeSize && deliveryPipeClass) {
      fetchPipeDimensions(deliveryPipeSize, deliveryPipeClass, "delivery");
    }
  }, [deliveryPipeSize, deliveryPipeClass]);

  useEffect(() => {
    if (fieldPipeSize && fieldPipeClass) {
      fetchPipeDimensions(fieldPipeSize, fieldPipeClass, "field");
    }
  }, [fieldPipeSize, fieldPipeClass]);

  useEffect(() => {
    if (
      suctionPipeSize ||
      deliveryPipeSize ||
      fieldPipeSize ||
      suctionPipeClass ||
      deliveryPipeClass ||
      fieldPipeClass
    ) {
      dataChangeCallbackEnabled.current = false;
      const timer = setTimeout(() => {
        dataChangeCallbackEnabled.current = true;
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [
    suctionPipeSize,
    deliveryPipeSize,
    fieldPipeSize,
    suctionPipeClass,
    deliveryPipeClass,
    fieldPipeClass,
  ]);

  useEffect(() => {
    if (selectedPump === "surface" && suctionPipeSize && suctionPipeClass) {
      fetchPipeDimensions(suctionPipeSize, suctionPipeClass, "suction");
    }
  }, [selectedPump, suctionPipeSize, suctionPipeClass]);

  useEffect(() => {

    const hasRequiredData = () => {
      if (selectedPump === "submersible") {

        return (
          deliveryPipeSize &&
          fieldPipeSize &&
          deliveryPipeClass &&
          fieldPipeClass &&
          !deliveryClassError &&
          !fieldClassError &&
          deliveryPipeDiameter > 0 &&
          fieldPipeDiameter > 0
        );
      } else {

        return (
          suctionPipeSize &&
          deliveryPipeSize &&
          fieldPipeSize &&
          suctionPipeClass &&
          deliveryPipeClass &&
          fieldPipeClass &&
          !suctionClassError &&
          !deliveryClassError &&
          !fieldClassError &&
          suctionPipeDiameter > 0 &&
          deliveryPipeDiameter > 0 &&
          fieldPipeDiameter > 0
        );
      }
    };

    if (
      dataChangeCallbackEnabled.current &&
      onDataChange &&
      hasRequiredData()
    ) {
      const dataToSend = {
        pumpType: selectedPump,
        deliveryPipeSize,
        deliveryPipeLength,
        deliveryPipeClass,
        deliveryPipeDiameter,
        fieldPipeSize,
        fieldPipeLength,
        fieldPipeClass,
        fieldPipeDiameter,
        flowRate: localLph / 1000,
        localLph,
        scenarioType: "sprinklers",
        pipeDiameter: deliveryPipeDiameter,
      };

      if (selectedPump === "surface") {
        dataToSend.suctionPipeSize = suctionPipeSize;
        dataToSend.suctionPipeLength = suctionPipeLength;
        dataToSend.suctionPipeClass = suctionPipeClass;
        dataToSend.suctionPipeDiameter = suctionPipeDiameter;
      }

      onDataChange(dataToSend);
      dataChangeCallbackEnabled.current = false;
    }
  }, [
    selectedPump,
    suctionPipeSize,
    suctionPipeLength,
    suctionPipeClass,
    suctionPipeDiameter,
    deliveryPipeSize,
    deliveryPipeLength,
    deliveryPipeClass,
    deliveryPipeDiameter,
    fieldPipeSize,
    fieldPipeLength,
    fieldPipeClass,
    fieldPipeDiameter,
    suctionClassError,
    deliveryClassError,
    fieldClassError,
    localLph,
    onDataChange,
  ]);

  const getDisplaySizeForHDPE = (size, material, sizeData = null) => {

    if (sizeData && typeof sizeData === 'object' && sizeData.display) {
      return sizeData.display;
    }

    if (typeof size === 'object' && size.display) {
      return size.display;
    }

    return `${size}"`;
  };

  const getMaterialFromClass = (classType) => {
    if (["CL6", "CL9", "CL12", "CL15"].includes(classType)) return "upvc";
    if (["EL", "L2", "M", "H"].includes(classType)) return "gi";
    if (["PN-06", "PN-08", "PN-10", "PN-12.5", "PN-16"].includes(classType))
      return "hdpe";
    return "";
  };

  const getMaterialIcon = (classType) => {
    if (["CL6", "CL9", "CL12", "CL15"].includes(classType)) return "🔩";
    if (["EL", "L2", "M", "H"].includes(classType)) return "🔧";
    if (["PN-06", "PN-08", "PN-10", "PN-12.5", "PN-16"].includes(classType))
      return "🧪";
    return "📏";
  };

  const getClassDisplayName = (classCode) => {
    const classMapping = {
      CL6: "CL6/B",
      CL9: "CL9/C",
      CL12: "CL12/D",
      CL15: "CL15/E",
    };

    return classMapping[classCode] || classCode;
  };

  const handlePumpChange = (e) => {
    setSelectedPump(e.target.value);
  };

  const handlePumpEfficiencyChange = (e) => {
    const rawInput = e.target.value;
    setPumpEfficiencyInput(rawInput);

    if (rawInput === "" || rawInput === ".") {
      setPumpEfficiency(60);
    } else {
      const parsedValue = parseFloat(rawInput);
      if (!isNaN(parsedValue) && parsedValue > 0 && parsedValue <= 100) {
        setPumpEfficiency(parsedValue);
      }
    }
  };

  const handlePumpEfficiencyBlur = () => {
    if (pumpEfficiencyInput === "" || pumpEfficiencyInput === ".") {
      setPumpEfficiencyInput("60");
      setPumpEfficiency(60);
    } else {
      const parsedValue = parseFloat(pumpEfficiencyInput);
      if (!isNaN(parsedValue) && parsedValue > 0 && parsedValue <= 100) {
        setPumpEfficiencyInput(parsedValue.toString());
        setPumpEfficiency(parsedValue);
      } else {
        setPumpEfficiencyInput("60");
        setPumpEfficiency(60);
      }
    }
  };

  const handleMotorEfficiencyChange = (e) => {
    const rawInput = e.target.value;
    setMotorEfficiencyInput(rawInput);

    if (rawInput === "" || rawInput === ".") {
      setMotorEfficiency(80);
    } else {
      const parsedValue = parseFloat(rawInput);
      if (!isNaN(parsedValue) && parsedValue > 0 && parsedValue <= 100) {
        setMotorEfficiency(parsedValue);
      }
    }
  };

  const handleMotorEfficiencyBlur = () => {
    if (motorEfficiencyInput === "" || motorEfficiencyInput === ".") {
      setMotorEfficiencyInput("80");
      setMotorEfficiency(80);
    } else {
      const parsedValue = parseFloat(motorEfficiencyInput);
      if (!isNaN(parsedValue) && parsedValue > 0 && parsedValue <= 100) {
        setMotorEfficiencyInput(parsedValue.toString());
        setMotorEfficiency(parsedValue);
      } else {
        setMotorEfficiencyInput("80");
        setMotorEfficiency(80);
      }
    }
  };

  const handleSolarEfficiencyChange = (e) => {
    const rawInput = e.target.value;
    setSolarEfficiencyInput(rawInput);

    if (rawInput === "" || rawInput === ".") {
      setSolarEfficiency(70);
    } else {
      const parsedValue = parseFloat(rawInput);
      if (!isNaN(parsedValue) && parsedValue > 0 && parsedValue <= 100) {
        setSolarEfficiency(parsedValue);
      }
    }
  };

  const handleSolarEfficiencyBlur = () => {
    if (solarEfficiencyInput === "" || solarEfficiencyInput === ".") {
      setSolarEfficiencyInput("70");
      setSolarEfficiency(70);
    } else {
      const parsedValue = parseFloat(solarEfficiencyInput);
      if (!isNaN(parsedValue) && parsedValue > 0 && parsedValue <= 100) {
        setSolarEfficiencyInput(parsedValue.toString());
        setSolarEfficiency(parsedValue);
      } else {
        setSolarEfficiencyInput("70");
        setSolarEfficiency(70);
      }
    }
  };

  const fetchPipeDimensions = async (inch, classType, pipeType) => {
    setLoading(true);

    if (pipeType === "suction") {
      setSuctionClassError(false);
      setSuctionErrorMessage("");
    } else if (pipeType === "delivery") {
      setDeliveryClassError(false);
      setDeliveryErrorMessage("");
    } else {
      setFieldClassError(false);
      setFieldErrorMessage("");
    }

    try {
      const response = await fetch(
        `${API_URL}/api/v1/pipe-utils/specs?inch=${encodeURIComponent(
          inch
        )}&class_type=${classType}`
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch pipe dimensions: ${response.statusText}`
        );
      }

      const data = await response.json();

      if (
        data.class_status ===
        "Unavailable Pipe Dia"
      ) {

        const errorMsg = `As per ${
          data.material?.toUpperCase() || "pipe"
        } Specifications, ${classType} doesn't exist for ${inch}" pipe diameter`;

        if (pipeType === "suction") {
          setSuctionClassError(true);
          setSuctionErrorMessage(errorMsg);
          setSuctionPipeDiameter(0);
          setSuctionPipeSpecs(null);
        } else if (pipeType === "delivery") {
          setDeliveryClassError(true);
          setDeliveryErrorMessage(errorMsg);
          setDeliveryPipeDiameter(0);
          setDeliveryPipeSpecs(null);
        } else {
          setFieldClassError(true);
          setFieldErrorMessage(errorMsg);
          setFieldPipeDiameter(0);
          setFieldPipeSpecs(null);
        }
        console.error(`${pipeType} pipe error:`, errorMsg);
        return;
      }

      if (data.value && data.class_status === "Available") {
        const thickness = parseFloat(data.value);
        let innerDiameter = 0;

        if (data.material === "hdpe") {

          const nominalOD = parseFloat(inch.replace(/\s/g, "")) || 0;
          const outerDiameterMM = nominalOD * 25.4;
          innerDiameter = outerDiameterMM - 2 * thickness;
        } else if (data.material === "gi") {

          if (data.mean) {
            innerDiameter = data.mean;
          } else {

            const nominalID = parseFloat(inch.replace(/\s/g, "")) || 0;
            innerDiameter = nominalID * 25.4;
          }
        } else {

          if (data.mean) {
            innerDiameter = data.mean - thickness;
          } else {

            innerDiameter = thickness * 10;
          }
        }

        if (innerDiameter <= 0) {
          const errorMsg = `Invalid pipe dimensions calculated for ${inch}" ${classType}`;
          if (pipeType === "suction") {
            setSuctionClassError(true);
            setSuctionErrorMessage(errorMsg);
            setSuctionPipeDiameter(0);
            setSuctionPipeSpecs(null);
          } else if (pipeType === "delivery") {
            setDeliveryClassError(true);
            setDeliveryErrorMessage(errorMsg);
            setDeliveryPipeDiameter(0);
            setDeliveryPipeSpecs(null);
          } else {
            setFieldClassError(true);
            setFieldErrorMessage(errorMsg);
            setFieldPipeDiameter(0);
            setFieldPipeSpecs(null);
          }
          return;
        }

        if (pipeType === "suction") {
          setSuctionPipeSpecs(data);
          setSuctionPipeDiameter(innerDiameter);
          setSuctionClassError(false);
          setSuctionErrorMessage("");
        } else if (pipeType === "delivery") {
          setDeliveryPipeSpecs(data);
          setDeliveryPipeDiameter(innerDiameter);
          setDeliveryClassError(false);
          setDeliveryErrorMessage("");
        } else {
          setFieldPipeSpecs(data);
          setFieldPipeDiameter(innerDiameter);
          setFieldClassError(false);
          setFieldErrorMessage("");
        }
      } else {

        const errorMsg = `Invalid response data for ${inch}" ${classType}`;
        console.error(`Unexpected response format:`, data);

        if (pipeType === "suction") {
          setSuctionClassError(true);
          setSuctionErrorMessage(errorMsg);
          setSuctionPipeDiameter(0);
          setSuctionPipeSpecs(null);
        } else if (pipeType === "delivery") {
          setDeliveryClassError(true);
          setDeliveryErrorMessage(errorMsg);
          setDeliveryPipeDiameter(0);
          setDeliveryPipeSpecs(null);
        } else {
          setFieldClassError(true);
          setFieldErrorMessage(errorMsg);
          setFieldPipeDiameter(0);
          setFieldPipeSpecs(null);
        }
      }
    } catch (err) {
      console.error(`Error fetching ${pipeType} pipe dimensions:`, err);
      setError(err.message || `Failed to fetch ${pipeType} pipe dimensions`);
      if (pipeType === "suction") {
        setSuctionPipeSpecs(null);
        setSuctionPipeDiameter(0);
      } else if (pipeType === "delivery") {
        setDeliveryPipeSpecs(null);
        setDeliveryPipeDiameter(0);
      } else {
        setFieldPipeSpecs(null);
        setFieldPipeDiameter(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const calculatePipeSpecs = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }

    setLoading(true);
    setErrorMessage("");

    if (selectedPump === "surface") {
      if (
        !suctionPipeSize ||
        !suctionPipeClass ||
        !deliveryPipeSize ||
        !deliveryPipeClass ||
        !fieldPipeSize ||
        !fieldPipeClass
      ) {
        setErrorMessage(
          "Please select pipe sizes and classes for all pipe sections"
        );
        return;
      }

      if (suctionClassError || deliveryClassError || fieldClassError) {
        setErrorMessage(
          "Please correct the pipe class errors before calculating"
        );
        return;
      }

      if (
        suctionPipeDiameter <= 0 ||
        deliveryPipeDiameter <= 0 ||
        fieldPipeDiameter <= 0
      ) {
        setErrorMessage(
          "Unable to Calculate Specs. Please check your selections."
        );
        return;
      }
    } else {

      if (
        !deliveryPipeSize ||
        !deliveryPipeClass ||
        !fieldPipeSize ||
        !fieldPipeClass
      ) {
        setErrorMessage(
          "Please select pipe sizes and classes for delivery and field pipe sections"
        );
        return;
      }

      if (deliveryClassError || fieldClassError) {
        setErrorMessage(
          "Please correct the pipe class errors before calculating"
        );
        return;
      }

      if (deliveryPipeDiameter <= 0 || fieldPipeDiameter <= 0) {
        setErrorMessage(
          "Unable to Calculate Specs. Please check your delivery and field pipe selections."
        );
        return;
      }
    }

    setLoading(true);
    setErrorMessage("");

    try {
      let suctionThickness = null;
      let deliveryThickness = null;
      let fieldThickness = null;

      if (selectedPump === "surface") {
        suctionThickness = extractThicknessFromSpecs(
          suctionPipeSpecs,
          suctionPipeClass
        );

        if (suctionThickness === null) {
          setErrorMessage(
            "Unable to extract suction pipe thickness. Please reselect pipe specifications."
          );
          return;
        }
      }

      deliveryThickness = extractThicknessFromSpecs(
        deliveryPipeSpecs,
        deliveryPipeClass
      );

      if (deliveryThickness === null) {
        setErrorMessage(
          "Unable to extract delivery pipe thickness. Please reselect pipe specifications."
        );
        return;
      }

      fieldThickness = extractThicknessFromSpecs(
        fieldPipeSpecs,
        fieldPipeClass
      );

      if (fieldThickness === null) {
        setErrorMessage(
          "Unable to extract field pipe thickness. Please reselect pipe specifications."
        );
        return;
      }

      const payload = {
        pumpType: selectedPump,
        deliveryPipeSize,
        deliveryPipeLength,
        deliveryPipeClass,
        deliveryPipeDiameter,
        deliveryThickness,
        fieldPipeSize,
        fieldPipeLength,
        fieldPipeClass,
        fieldPipeDiameter,
        fieldThickness,
        flowRate: localLph / 1000,
        lph: localLph,
      };

      if (selectedPump === "surface") {
        payload.suctionPipeSize = suctionPipeSize;
        payload.suctionPipeLength = suctionPipeLength;
        payload.suctionPipeClass = suctionPipeClass;
        payload.suctionPipeDiameter = suctionPipeDiameter;
        payload.suctionThickness = suctionThickness;
      }

      const response = await fetch(
        `${API_URL}/api/v1/pipe-utils/calculate-sprinkler-specs`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to calculate pipe specs: ${response.statusText}`
        );
      }

      const data = await response.json();
      setCalculationResults(data);

      if (data.error) {
        setErrorMessage(data.error);
        return;
      }

      const powerCalcs = calculatePowerRequirementsWithData(data);

      if (powerCalcs && onCalculationResults) {
        onCalculationResults(data, powerCalcs);
      }

      setErrorMessage("");
    } catch (err) {
      console.error("Error calculating pipe specs:", err);
      setErrorMessage(err.message || "Failed to Calculate Specs");
    } finally {
      setLoading(false);
    }
  };

  const calculatePowerRequirementsWithData = (calculationData) => {
    if (!calculationData) return null;

    let suctionLoss = 0;
    if (selectedPump === "surface" && calculationData.suction_pipe) {
      suctionLoss =
        parseFloat(calculationData.suction_pipe?.friction_losses_m) || 0;
    }

    const deliveryLoss =
      parseFloat(calculationData.delivery_pipe?.friction_losses_m) || 0;
    const fieldLoss =
      parseFloat(calculationData.field_pipe?.friction_losses_m) || 0;
    const frictionLosses = suctionLoss + deliveryLoss + fieldLoss;
    const minorLosses = frictionLosses * 0.2;

    let tdh = frictionLosses + minorLosses;
    if (selectedPump === "surface") {
      tdh +=
        parseFloat(suctionHead) +
        parseFloat(deliveryHead) +
        parseFloat(elevationDiff);
    } else {
      tdh += parseFloat(deliveryHead) + parseFloat(elevationDiff);
    }

    const hpw = (localLph * tdh) / 270000;
    const hpp = hpw / (pumpEfficiency / 100);
    const hpm = hpp;
    const hps = hpm / (motorEfficiency / 100);
    const kwPvOutput = (hps * 746) / 1000;
    const kwPvInput = kwPvOutput / (solarEfficiency / 100);

    return {
      frictionLosses,
      minorLosses,
      tdh,
      hpw,
      hpp,
      hpm,
      hps,
      kwPvOutput,
      kwPvInput,
    };
  };

  const handleDaysChange = (e) => {
    const rawInput = e.target.value;
    setDaysInputValue(rawInput);

    if (rawInput === "" || rawInput === ".") {
      setNumberOfDays(30);
    } else {
      const parsedValue = parseFloat(rawInput);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        setNumberOfDays(parsedValue);
      }
    }
  };

  const handleDaysBlur = () => {
    if (daysInputValue === "" || daysInputValue === ".") {
      setDaysInputValue("30");
      setNumberOfDays(30);
    } else {
      const parsedValue = parseFloat(daysInputValue);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        setDaysInputValue(parsedValue.toString());
        setNumberOfDays(parsedValue);
      } else {
        setDaysInputValue("30");
        setNumberOfDays(30);
      }
    }
  };

  const handleHoursChange = (e) => {
    const rawInput = e.target.value;
    setHoursInputValue(rawInput);

    if (rawInput === "" || rawInput === ".") {
      setNumberOfHours(6);
    } else {
      const parsedValue = parseFloat(rawInput);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        setNumberOfHours(parsedValue);
      }
    }
  };

  const handleHoursBlur = () => {
    if (hoursInputValue === "" || hoursInputValue === ".") {
      setHoursInputValue("6");
      setNumberOfHours(6);
    } else {
      const parsedValue = parseFloat(hoursInputValue);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        setHoursInputValue(parsedValue.toString());
        setNumberOfHours(parsedValue);
      } else {
        setHoursInputValue("6");
        setNumberOfHours(6);
      }
    }
  };

  const handleLphChange = (e) => {
    const rawInput = e.target.value;
    setLphInputValue(rawInput);

    if (rawInput === "" || rawInput === ".") {
      setLocalLph(0);
    } else {
      const parsedValue = parseFloat(rawInput);
      if (!isNaN(parsedValue) && parsedValue >= 0) {
        setLocalLph(parsedValue);
      }
    }
  };

  const handleLphBlur = () => {

    if (lphInputValue === "" || lphInputValue === ".") {
      setLphInputValue("0.00");
      setLocalLph(0);
    } else {
      const parsedValue = parseFloat(lphInputValue);
      if (!isNaN(parsedValue)) {

        const formattedValue = parsedValue.toFixed(2);
        setLphInputValue(formattedValue);
        setLocalLph(parseFloat(formattedValue));
      } else {

        setLphInputValue("0.00");
        setLocalLph(0);
      }
    }
  };

  const cardStyle = {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "6px",
    marginBottom: "20px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    border: "1px solid #e8e8e8",
    flex: "1",
    position: "relative",
  };

  const cardHeaderStyle = {
    borderBottom: "1px solid #e8e8e8",
    paddingBottom: "10px",
    marginBottom: "15px",
    fontWeight: "bold",
    fontSize: "16px",
    color: "#333",
  };

  const classErrorStyle = {
    color: "#ff4d4f",
    fontSize: "12px",
    marginTop: "5px",
    backgroundColor: "#fff2f0",
    padding: "10px",
    border: "1px solid #ffccc7",
    borderRadius: "4px",
  };

  const radioGroupStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    marginBottom: "20px",
  };

  const radioOptionStyle = {
    display: "flex",
    alignItems: "center",
    padding: "12px",
    border: "2px solid #e0e0e0",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    backgroundColor: "white",
  };

  const radioOptionActiveStyle = {
    ...radioOptionStyle,
    borderColor: "#2196F3",
    backgroundColor: "#f0f8ff",
  };

  const radioInputStyle = {
    marginRight: "12px",
    transform: "scale(1.2)",
  };

  const labelStyle = {
    fontWeight: "500",
    fontSize: "16px",
    cursor: "pointer",
  };

  const descriptionStyle = {
    fontSize: "14px",
    color: "#666",
    marginTop: "5px",
  };

  return (
    <div>
      {}
      <div style={radioGroupStyle}>
        {}
        <div
          style={
            selectedPump === "submersible"
              ? radioOptionActiveStyle
              : radioOptionStyle
          }
          onClick={() => handlePumpChange({ target: { value: "submersible" } })}
        >
          <input
            type="radio"
            id="submersible"
            name="pumpType"
            value="submersible"
            checked={selectedPump === "submersible"}
            onChange={handlePumpChange}
            style={radioInputStyle}
          />
          <div>
            <label htmlFor="submersible" style={labelStyle}>
              Submersible Pump
            </label>
            <div style={descriptionStyle}>
              Pump installed below water level, ideal for deep water sources and sprinkler systems
            </div>
          </div>
        </div>

        {}
        <div
          style={
            selectedPump === "surface"
              ? radioOptionActiveStyle
              : radioOptionStyle
          }
          onClick={() => handlePumpChange({ target: { value: "surface" } })}
        >
          <input
            type="radio"
            id="surface"
            name="pumpType"
            value="surface"
            checked={selectedPump === "surface"}
            onChange={handlePumpChange}
            style={radioInputStyle}
          />
          <div>
            <label htmlFor="surface" style={labelStyle}>
              Surface Pump
            </label>
            <div style={descriptionStyle}>
              Pump installed above ground, suitable for shallow water sources and sprinkler irrigation
            </div>
          </div>
        </div>
      </div>
      <p style={{ marginBottom: "20px" }}>
        Crop LPH Requirement:{" "}
        <label style={GlobalStyles.label}>
          <input
            type="text"
            name="lphVal"
            value={lphInputValue}
            onChange={handleLphChange}
            onBlur={handleLphBlur}
            style={{
              ...GlobalStyles.input,
              width: "120px",
              display: "inline-block",
              marginLeft: "8px",
              marginRight: "8px",
            }}
            inputMode="decimal"
            min="0"
          />
          LPH
        </label>
        {}
      </p>
      {loading && (
        <p style={GlobalStyles.loading}>Loading pipe specifications...</p>
      )}
      {error && <p style={GlobalStyles.error}>{error}</p>}
      {errorMessage && <p style={GlobalStyles.error}>{errorMessage}</p>}

      {}
      <div
        style={{
          display: "flex",
          gap: "15px",
          marginBottom: "20px",
          flexWrap: "nowrap",
        }}
      >
        {}
        {selectedPump === "surface" && (
          <div style={{ ...cardStyle, flex: "1" }}>
            <div style={cardHeaderStyle}>Suction Pipe (Pump Intake)</div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "15px" }}
            >
              <div>
                <label style={GlobalStyles.label}>
                  Pressure Class (CL):
                  <select
                    style={{
                      ...GlobalStyles.select,
                      borderColor: suctionClassError ? "#ff4d4f" : undefined,
                    }}
                    value={suctionPipeClass}
                    onChange={(e) => {
                      const newClass = e.target.value;
                      setSuctionPipeClass(newClass);
                      setSuctionClassError(false);
                      setSuctionErrorMessage("");
                    }}
                  >
                    <option value="">Select pipe class</option>

                    <optgroup label="uPVC Pipe">
                      {pipeClasses
                        .filter((cls) =>
                          ["CL6", "CL9", "CL12", "CL15"].includes(cls)
                        )
                        .map((cls) => (
                          <option key={cls} value={cls}>
                            {getClassDisplayName(cls)}
                          </option>
                        ))}
                    </optgroup>

                    <optgroup label="GI Pipe">
                      {pipeClasses
                        .filter((cls) => ["EL", "L2", "M", "H"].includes(cls))
                        .map((cls) => (
                          <option key={cls} value={cls}>
                            {getClassDisplayName(cls)}
                          </option>
                        ))}
                    </optgroup>

                    <optgroup label="HDPE Pipe">
                      {pipeClasses
                        .filter((cls) =>
                          [
                            "PN-06",
                            "PN-08",
                            "PN-10",
                            "PN-12.5",
                            "PN-16",
                          ].includes(cls)
                        )
                        .map((cls) => (
                          <option key={cls} value={cls}>
                            {getClassDisplayName(cls)}
                          </option>
                        ))}
                    </optgroup>
                  </select>
                </label>
              </div>
              <div>
                <label style={GlobalStyles.label}>
                  Diameter:
                  {suctionPipeClass && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#6c757d",
                        fontWeight: "400",
                        marginLeft: "8px",
                      }}
                    >
                      {getMaterialIcon(suctionPipeClass)} filtered for{" "}
                      {suctionPipeClass} (
                      {getMaterialFromClass(suctionPipeClass)})
                    </span>
                  )}
                  <select
                    style={{
                      ...GlobalStyles.select,
                      borderColor: suctionClassError ? "#ff4d4f" : undefined,
                    }}
                    value={suctionPipeSize}
                    onChange={(e) => {
                      setSuctionPipeSize(e.target.value);
                      setSuctionClassError(false);
                      setSuctionErrorMessage("");
                    }}
                    disabled={!suctionPipeClass}
                  >
                    <option value="">
                      {suctionPipeClass
                        ? "Select pipe size"
                        : "Select pipe class first"}
                    </option>
                    {suctionPipeClass && suctionFilteredSizes.length === 0 ? (
                      <option value="" disabled>
                        No sizes available for {suctionPipeClass}
                      </option>
                    ) : (
                      suctionFilteredSizes.map((size) => {
                        const material = getMaterialFromClass(suctionPipeClass);
                        const displaySize = getDisplaySizeForHDPE(
                          size,
                          material
                        );

                        const sizeValue = typeof size === 'object' ? size.inch : size;
                        const sizeKey = typeof size === 'object' ? size.inch : size;

                        return (
                          <option key={sizeKey} value={sizeValue}>
                            {displaySize}
                          </option>
                        );
                      })
                    )}
                  </select>
                  {!suctionPipeClass && (
                    <div
                      style={{
                        marginTop: "6px",
                        fontSize: "11px",
                        color: "#6c757d",
                        fontStyle: "italic",
                      }}
                    >
                      💡 Select a pipe class above to see available sizes
                    </div>
                  )}
                  {suctionPipeClass && suctionFilteredSizes.length === 0 && (
                    <div
                      style={{
                        marginTop: "6px",
                        padding: "8px 12px",
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffeaa7",
                        borderRadius: "4px",
                        fontSize: "12px",
                        color: "#856404",
                      }}
                    >
                      ⚠️ No pipe sizes available for class {suctionPipeClass}.
                      Try selecting a different class.
                    </div>
                  )}
                </label>
              </div>
              <div>
                <label style={GlobalStyles.label}>
                  Suction Pipe Length (mtr):
                  <input
                    type="number"
                    style={GlobalStyles.input}
                    value={suctionPipeLength}
                    onChange={(e) =>
                      setSuctionPipeLength(Number(e.target.value))
                    }
                    min="1"
                    max="1000"
                  />
                </label>
              </div>
              <div>
                <label style={GlobalStyles.label}>
                  Suction Head (mtr):
                  <input
                    type="number"
                    style={GlobalStyles.input}
                    value={suctionHead}
                    onChange={(e) => setSuctionHead(Number(e.target.value))}
                    min="1"
                    max="1000"
                  />
                </label>
              </div>

              {suctionClassError && suctionErrorMessage && (
                <div style={classErrorStyle}>{suctionErrorMessage}</div>
              )}
            </div>
          </div>
        )}

        {}
        <div style={{ ...cardStyle, flex: "1" }}>
          <div style={cardHeaderStyle}>Delivery Pipe (Main Line)</div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "15px" }}
          >
            <div>
              <label style={GlobalStyles.label}>
                Pressure Class (CL):
                <select
                  style={{
                    ...GlobalStyles.select,
                    borderColor: deliveryClassError ? "#ff4d4f" : undefined,
                  }}
                  value={deliveryPipeClass}
                  onChange={(e) => {
                    const newClass = e.target.value;
                    setDeliveryPipeClass(newClass);
                    setDeliveryClassError(false);
                    setDeliveryErrorMessage("");
                  }}
                >
                  <option value="">Select pipe class</option>

                  <optgroup label="uPVC Pipe">
                    {pipeClasses
                      .filter((cls) =>
                        ["CL6", "CL9", "CL12", "CL15"].includes(cls)
                      )
                      .map((cls) => (
                        <option key={cls} value={cls}>
                          {getClassDisplayName(cls)}
                        </option>
                      ))}
                  </optgroup>

                  <optgroup label="GI Pipe">
                    {pipeClasses
                      .filter((cls) => ["EL", "L2", "M", "H"].includes(cls))
                      .map((cls) => (
                        <option key={cls} value={cls}>
                          {getClassDisplayName(cls)}
                        </option>
                      ))}
                  </optgroup>

                  <optgroup label="HDPE Pipe">
                    {pipeClasses
                      .filter((cls) =>
                        [
                          "PN-06",
                          "PN-08",
                          "PN-10",
                          "PN-12.5",
                          "PN-16",
                        ].includes(cls)
                      )
                      .map((cls) => (
                        <option key={cls} value={cls}>
                          {getClassDisplayName(cls)}
                        </option>
                      ))}
                  </optgroup>
                </select>
              </label>
            </div>
            <div>
              <label style={GlobalStyles.label}>
                Diameter:
                {deliveryPipeClass && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      fontWeight: "400",
                      marginLeft: "8px",
                    }}
                  >
                    {getMaterialIcon(deliveryPipeClass)} filtered for{" "}
                    {deliveryPipeClass} (
                    {getMaterialFromClass(deliveryPipeClass)})
                  </span>
                )}
                <select
                  style={{
                    ...GlobalStyles.select,
                    borderColor: deliveryClassError ? "#ff4d4f" : undefined,
                  }}
                  value={deliveryPipeSize}
                  onChange={(e) => {
                    setDeliveryPipeSize(e.target.value);
                    setDeliveryClassError(false);
                    setDeliveryErrorMessage("");
                  }}
                  disabled={!deliveryPipeClass}
                >
                  <option value="">
                    {deliveryPipeClass
                      ? "Select pipe size"
                      : "Select pipe class first"}
                  </option>
                  {deliveryPipeClass && deliveryFilteredSizes.length === 0 ? (
                    <option value="" disabled>
                      No sizes available for {deliveryPipeClass}
                    </option>
                  ) : (
                    deliveryFilteredSizes.map((size) => {
                      const material = getMaterialFromClass(deliveryPipeClass);
                      const displaySize = getDisplaySizeForHDPE(size, material);

                      const sizeValue = typeof size === 'object' ? size.inch : size;
                      const sizeKey = typeof size === 'object' ? size.inch : size;

                      return (
                        <option key={sizeKey} value={sizeValue}>
                          {displaySize}
                        </option>
                      );
                    })
                  )}
                </select>
                {!deliveryPipeClass && (
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: "11px",
                      color: "#6c757d",
                      fontStyle: "italic",
                    }}
                  >
                    💡 Select a pipe class above to see available sizes
                  </div>
                )}
                {deliveryPipeClass && deliveryFilteredSizes.length === 0 && (
                  <div
                    style={{
                      marginTop: "6px",
                      padding: "8px 12px",
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffeaa7",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#856404",
                    }}
                  >
                    ⚠️ No pipe sizes available for class {deliveryPipeClass}.
                    Try selecting a different class.
                  </div>
                )}
              </label>
            </div>
            <div>
              <label style={GlobalStyles.label}>
                Delivery Pipe Length (mtr):
                <input
                  type="number"
                  style={GlobalStyles.input}
                  value={deliveryPipeLength}
                  onChange={(e) =>
                    setDeliveryPipeLength(Number(e.target.value))
                  }
                  min="1"
                  max="1000"
                />
              </label>
            </div>
            <div>
              <label style={GlobalStyles.label}>
                Delivery Head (mtr):
                <input
                  type="number"
                  style={GlobalStyles.input}
                  value={deliveryHead}
                  onChange={(e) => setDeliveryHead(Number(e.target.value))}
                  min="1"
                  max="1000"
                />
              </label>
            </div>

            {deliveryClassError && deliveryErrorMessage && (
              <div style={classErrorStyle}>{deliveryErrorMessage}</div>
            )}
          </div>
        </div>

        {}
        <div style={{ ...cardStyle, flex: "1" }}>
          <div style={cardHeaderStyle}>Field Pipe (Distribution)</div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "15px" }}
          >
            <div>
              <label style={GlobalStyles.label}>
                Pressure Class (CL):
                <select
                  style={{
                    ...GlobalStyles.select,
                    borderColor: fieldClassError ? "#ff4d4f" : undefined,
                  }}
                  value={fieldPipeClass}
                  onChange={(e) => {
                    const newClass = e.target.value;
                    setFieldPipeClass(newClass);
                    setFieldClassError(false);
                    setFieldErrorMessage("");
                  }}
                >
                  <option value="">Select pipe class</option>

                  <optgroup label="uPVC Pipe">
                    {pipeClasses
                      .filter((cls) =>
                        ["CL6", "CL9", "CL12", "CL15"].includes(cls)
                      )
                      .map((cls) => (
                        <option key={cls} value={cls}>
                          {getClassDisplayName(cls)}
                        </option>
                      ))}
                  </optgroup>

                  <optgroup label="GI Pipe">
                    {pipeClasses
                      .filter((cls) => ["EL", "L2", "M", "H"].includes(cls))
                      .map((cls) => (
                        <option key={cls} value={cls}>
                          {getClassDisplayName(cls)}
                        </option>
                      ))}
                  </optgroup>

                  <optgroup label="HDPE Pipe">
                    {pipeClasses
                      .filter((cls) =>
                        [
                          "PN-06",
                          "PN-08",
                          "PN-10",
                          "PN-12.5",
                          "PN-16",
                        ].includes(cls)
                      )
                      .map((cls) => (
                        <option key={cls} value={cls}>
                          {getClassDisplayName(cls)}
                        </option>
                      ))}
                  </optgroup>
                </select>
              </label>
            </div>
            <div>
              <label style={GlobalStyles.label}>
                Diameter:
                {fieldPipeClass && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      fontWeight: "400",
                      marginLeft: "8px",
                    }}
                  >
                    {getMaterialIcon(fieldPipeClass)} filtered for{" "}
                    {fieldPipeClass} ({getMaterialFromClass(fieldPipeClass)})
                  </span>
                )}
                <select
                  style={{
                    ...GlobalStyles.select,
                    borderColor: fieldClassError ? "#ff4d4f" : undefined,
                  }}
                  value={fieldPipeSize}
                  onChange={(e) => {
                    setFieldPipeSize(e.target.value);
                    setFieldClassError(false);
                    setFieldErrorMessage("");
                  }}
                  disabled={!fieldPipeClass}
                >
                  <option value="">
                    {fieldPipeClass
                      ? "Select pipe size"
                      : "Select pipe class first"}
                  </option>
                  {fieldPipeClass && fieldFilteredSizes.length === 0 ? (
                    <option value="" disabled>
                      No sizes available for {fieldPipeClass}
                    </option>
                  ) : (
                    fieldFilteredSizes.map((size) => {
                      const material = getMaterialFromClass(fieldPipeClass);
                      const displaySize = getDisplaySizeForHDPE(size, material);

                      const sizeValue = typeof size === 'object' ? size.inch : size;
                      const sizeKey = typeof size === 'object' ? size.inch : size;

                      return (
                        <option key={sizeKey} value={sizeValue}>
                          {displaySize}
                        </option>
                      );
                    })
                  )}
                </select>
                {!fieldPipeClass && (
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: "11px",
                      color: "#6c757d",
                      fontStyle: "italic",
                    }}
                  >
                    💡 Select a pipe class above to see available sizes
                  </div>
                )}
                {fieldPipeClass && fieldFilteredSizes.length === 0 && (
                  <div
                    style={{
                      marginTop: "6px",
                      padding: "8px 12px",
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffeaa7",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#856404",
                    }}
                  >
                    ⚠️ No pipe sizes available for class {fieldPipeClass}. Try
                    selecting a different class.
                  </div>
                )}
              </label>
            </div>
            <div>
              <label style={GlobalStyles.label}>
                Field Pipe Length (mtr):
                <input
                  type="number"
                  style={GlobalStyles.input}
                  value={fieldPipeLength}
                  onChange={(e) => setFieldPipeLength(Number(e.target.value))}
                  min="1"
                  max="1000"
                />
              </label>
            </div>
            <div>
              <label style={GlobalStyles.label}>
                Elevation Difference (if any, mtr):
                <input
                  type="number"
                  style={GlobalStyles.input}
                  value={elevationDiff}
                  onChange={(e) => setElevationDiff(Number(e.target.value))}
                  max="1000"
                />
              </label>
            </div>

            {fieldClassError && fieldErrorMessage && (
              <div style={classErrorStyle}>{fieldErrorMessage}</div>
            )}
          </div>
        </div>
      </div>

      {}
      <div
        style={{
          backgroundColor: "#f0f7ff",
          padding: "15px",
          borderRadius: "6px",
          border: "1px solid #d0e0ff",
          marginBottom: "20px",
        }}
      >
        <h4 style={{ marginTop: 0, marginBottom: "15px", color: "#333" }}>
          System Efficiencies
        </h4>
        <div style={{ display: "flex", gap: "15px" }}>
          <div style={{ flex: 1 }}>
            <label style={GlobalStyles.label}>
              Pump Efficiency (%):
              <input
                style={GlobalStyles.input}
                value={pumpEfficiencyInput}
                onChange={handlePumpEfficiencyChange}
                onBlur={handlePumpEfficiencyBlur}
                type="text"
                inputMode="decimal"
                min="1"
                max="100"
              />
            </label>
          </div>
          <div style={{ flex: 1 }}>
            <label style={GlobalStyles.label}>
              Motor Efficiency (%):
              <input
                style={GlobalStyles.input}
                value={motorEfficiencyInput}
                onChange={handleMotorEfficiencyChange}
                onBlur={handleMotorEfficiencyBlur}
                type="text"
                inputMode="decimal"
                min="1"
                max="100"
              />
            </label>
          </div>
          <div style={{ flex: 1 }}>
            <label style={GlobalStyles.label}>
              Solar Efficiency (%):
              <input
                style={GlobalStyles.input}
                value={solarEfficiencyInput}
                onChange={handleSolarEfficiencyChange}
                onBlur={handleSolarEfficiencyBlur}
                type="text"
                inputMode="decimal"
                min="1"
                max="100"
              />
            </label>
          </div>
        </div>
      </div>

      {}
      <div
        style={{
          padding: "15px",
          borderRadius: "6px",
          border: "1px solid #e8e8e8",
          marginBottom: "20px",
        }}
      >
        <h4 style={{ marginTop: 0, marginBottom: "15px", color: "#333" }}>
          Irrigation Scheduling
        </h4>

        <div
          style={{
            display: "flex",
            gap: "15px",
            marginBottom: "15px",
            alignItems: "flex-end",
          }}
        >
          <div style={{ flex: 1 }}>
            <label style={GlobalStyles.label}>
              Irrigation days (per month):
              <input
                style={GlobalStyles.input}
                value={daysInputValue}
                onChange={handleDaysChange}
                onBlur={handleDaysBlur}
                type="text"
                inputMode="decimal"
                min="1"
              />
            </label>
          </div>
          <div style={{ flex: 1 }}>
            <label style={GlobalStyles.label}>
              Irrigation Hours (per day):
              <input
                style={GlobalStyles.input}
                value={hoursInputValue}
                onChange={handleHoursChange}
                onBlur={handleHoursBlur}
                type="text"
                inputMode="decimal"
                min="1"
                max="24"
              />
            </label>
          </div>
        </div>
      </div>

      {}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <button
          style={{
            padding: "10px 20px",
            backgroundColor:
              (selectedPump === "surface" &&
                (suctionClassError || deliveryClassError || fieldClassError)) ||
              (selectedPump === "submersible" &&
                (deliveryClassError || fieldClassError))
                ? "#f5f5f5"
                : "#007bff",
            color:
              (selectedPump === "surface" &&
                (suctionClassError || deliveryClassError || fieldClassError)) ||
              (selectedPump === "submersible" &&
                (deliveryClassError || fieldClassError))
                ? "#999"
                : "white",
            border: "none",
            borderRadius: "4px",
            cursor:
              (selectedPump === "surface" &&
                (suctionClassError || deliveryClassError || fieldClassError)) ||
              (selectedPump === "submersible" &&
                (deliveryClassError || fieldClassError))
                ? "not-allowed"
                : "pointer",
            fontSize: "16px",
          }}
          onClick={calculatePipeSpecs}
          disabled={
            (selectedPump === "surface" &&
              (suctionClassError || deliveryClassError || fieldClassError)) ||
            (selectedPump === "submersible" &&
              (deliveryClassError || fieldClassError))
          }
        >
          Calculate Pump & Power Requirements
        </button>
      </div>
    </div>
  );
};

export default Scenario2;
